/**
 * wrapTextChild — wraps string/number children in a `<span>` so there is
 * never a bare text node as a direct child. Browser page translation replaces
 * text nodes with `<font>` wrappers; React then throws `NotFoundError:
 * insertBefore` when inserting a conditional sibling before the moved text
 * node. A wrapper span keeps the text node inside an element React owns.
 *
 * Element children (JSX) pass through unchanged — they are already element
 * nodes and are not moved by the translator.
 *
 * Extracted from Button.wrapLabel (#349b) so every primitive wrap site uses
 * a single implementation.
 */
import { type ReactNode, type ReactElement, isValidElement, Children, Fragment } from 'react';

export function wrapTextChild(children: ReactNode, className?: string): ReactNode {
  if (children == null || children === true || children === false) return children;

  // A single string or number — wrap it.
  if (typeof children === 'string' || typeof children === 'number') {
    return <span className={className}>{children}</span>;
  }

  // A single React element — check for Fragments first.
  if (isValidElement(children)) {
    // A Fragment is not an element the caller owns — unwrap it and wrap its
    // text children individually, preserving element children and order.
    // (#419: Fragment labels on Button/Chip/ActionButton produced bare text
    // nodes that crashed under page translation.)
    if (children.type === Fragment) {
      return unwrapFragment(children as ReactElement<{ children?: ReactNode }>, className);
    }
    return children;
  }

  // An array: if ANY element is a string or number, wrap those.
  if (Array.isArray(children)) {
    const hasText = children.some((c) => typeof c === 'string' || typeof c === 'number');
    if (hasText) {
      return <span className={className}>{children}</span>;
    }
    return children;
  }

  // Iterable children (Children.toArray normalizes): check for text.
  const arr = Children.toArray(children);
  const hasText = arr.some((c) => typeof c === 'string' || typeof c === 'number');
  if (hasText) {
    return <span className={className}>{children}</span>;
  }
  return children;
}

/**
 * Recursively unwrap a Fragment element: wrap each text/number child in a
 * `<span>`, keep element children as they are, handle nested Fragments.
 * Preserves order and keys.
 */
function unwrapFragment(fragment: ReactElement<{ children?: ReactNode }>, className?: string): ReactNode {
  const kids = Children.toArray(fragment.props.children);
  // Map every child: wrap text in <span>, recurse into nested Fragments,
  // pass elements through. Children.toArray assigns stable keys (.0, .1, …)
  // so element children already carry keys; text children get index-based keys.
  // Return as an array (valid ReactNode) — NOT a Fragment, because a keyless
  // Fragment inside a mapped sibling list triggers a React key warning.
  let textIndex = 0;
  return kids.map((c) => {
    if (typeof c === 'string' || typeof c === 'number') {
      return <span className={className} key={`_wt${textIndex++}`}>{c}</span>;
    }
    if (isValidElement(c) && c.type === Fragment) return unwrapFragment(c as ReactElement<{ children?: ReactNode }>, className);
    return c;
  });
}
